/**
 * Stats table with totals below headers, then data rows.
 * Supports grouping with collapsible sections. Generic for drops and lists.
 * For Media Devoted | Created By Ciro Autiero
 */

import { Fragment, type FC, type ReactNode } from 'react';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  ChevronRight,
  Info,
  Loader2,
} from 'lucide-react';

import { Tooltip } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { GenericStatsRow } from '../shared/generic-stats.types';

import { StatsRow } from './StatsRow';
import type { ColumnDef } from '../shared/constants';
import { FIRST_COLUMN_WIDTH_PX } from '../shared/constants';
import type { StatsGroupGeneric } from '../hooks';
import { formatMetricValue, getVoluumValueForDisplay } from '../shared';

/** Full Voluum pass (e.g. Phone tab limit “All”) — totals only, no extra rows. */
export interface StatsTableFullDatasetAggregate {
  loading: boolean;
  error: boolean;
  rowCount: number | null;
  totals: Record<string, number> | null;
}

interface StatsTableProps {
  rows: GenericStatsRow[];
  totals: Record<string, number>;
  /** When displayed rows < total, totals for the visible subset. Shown as "Showing (n)". */
  filteredTotals?: Record<string, number> | null;
  /** Count of displayed rows. When < totalRowCount, second totals row is shown. */
  filteredRowCount?: number;
  /** When set, renders grouped view with headers. */
  groups: StatsGroupGeneric[] | null;
  collapsedGroups: Set<string>;
  onToggleGroup: (key: string) => void;
  /** Number of drops the totals aggregate (e.g. 217). Shown as "Total (217)". */
  totalRowCount?: number;
  /** Custom label for totals row (e.g. "Total (5 drops) (23 texts)"). Overrides totalRowCount when set. */
  totalLabel?: string;
  /** Tooltip for the "(X drops)" part when totalLabel has that format (e.g. texts tab). */
  totalLabelDropsTooltip?: string;
  /** Report currency (e.g. USD, EUR) for money columns */
  currency?: string;
  columnDefs: ColumnDef[];
  sort: { field: string; dir: 'asc' | 'desc' } | null;
  onSortClick: (field: string) => void;
  selectedRows: GenericStatsRow[];
  setSelectedRows: (updater: (prev: GenericStatsRow[]) => GenericStatsRow[]) => void;
  /** Renders the first (label) column for each row */
  renderFirstCell: (row: GenericStatsRow) => ReactNode;
  /** Header for first column (e.g. "Drop List" or "List") */
  firstColumnLabel: string;
  /** Sort key when clicking first column (e.g. "dropList" for drops, "list" for lists) */
  firstColumnSortKey: string;
  /** Row unit label for group header (e.g. "drop" or "row") */
  rowUnitLabel?: string;
  /** Optional responsive min-width for first column (e.g. "min-w-[280px] md:min-w-[480px]") */
  firstColumnClassName?: string;
  fullDatasetAggregate?: StatsTableFullDatasetAggregate;
  /** Phone “All”: bar filters changed but full-dataset query not applied yet. */
  fullDatasetFiltersStale?: boolean;
}

function renderTotalsCells(
  totals: Record<string, number>,
  columnDefs: ColumnDef[],
  currency: string,
) {
  return columnDefs.map((col) => {
    const v = getVoluumValueForDisplay(totals, col.key);
    const formatted = formatMetricValue(v, col, currency);
    const numVal = typeof v === 'number' ? v : null;
    const hint = col.colorHint;
    const accent = 'columnAccent' in col ? (col as { columnAccent?: 'conv' | 'reg' | 'ftd' }).columnAccent : undefined;
    let colorClass = 'text-foreground font-semibold';
    let valueStyle: React.CSSProperties | undefined;
    if (accent && numVal != null && numVal > 0) {
      const varName =
        accent === 'conv' ? '--stat-conv-value' : accent === 'reg' ? '--stat-reg-value' : '--stat-ftd-value';
      valueStyle = { color: `hsl(var(${varName}))` };
    } else if (hint === 'money' && numVal != null) {
      if (numVal > 0) colorClass = 'text-success font-semibold';
      else if (numVal < 0) colorClass = 'text-destructive font-semibold';
    } else if (hint === 'metric' && numVal != null && numVal > 0) {
      colorClass = 'text-primary font-semibold';
    } else if (hint === 'negative' && numVal != null) {
      colorClass = 'text-destructive font-semibold';
    }
    return (
      <td
        key={col.key}
        className={cn('min-w-[4.5rem] px-2 py-2.5 text-right tabular-nums whitespace-nowrap', colorClass)}
        style={valueStyle}
      >
        {formatted}
      </td>
    );
  });
}

export const StatsTable: FC<StatsTableProps> = ({
  rows,
  totals,
  filteredTotals,
  filteredRowCount,
  groups,
  collapsedGroups,
  onToggleGroup,
  totalRowCount,
  totalLabel,
  totalLabelDropsTooltip,
  currency = 'USD',
  columnDefs,
  sort,
  onSortClick,
  selectedRows,
  setSelectedRows,
  renderFirstCell,
  firstColumnLabel,
  firstColumnSortKey = 'dropList',
  rowUnitLabel = 'row',
  firstColumnClassName,
  fullDatasetAggregate,
  fullDatasetFiltersStale,
}) => {
  const firstColStyle = firstColumnClassName
    ? undefined
    : { width: FIRST_COLUMN_WIDTH_PX, minWidth: FIRST_COLUMN_WIDTH_PX };
  const firstColClass = firstColumnClassName ?? '';
  const isGrouped = groups != null && groups.length > 0;
  const hasTotals = Object.keys(totals).length > 0;
  const totalN = typeof totalRowCount === 'number' && totalRowCount >= 0 ? totalRowCount : null;
  const showFilteredRow =
    hasTotals &&
    filteredTotals &&
    typeof filteredRowCount === 'number' &&
    totalN != null &&
    filteredRowCount < totalN;

  const totalsRowBg = 'color-mix(in srgb, hsl(var(--primary)) 15%, hsl(var(--card)))';
  const filteredRowBg = 'color-mix(in srgb, hsl(var(--primary)) 8%, hsl(var(--card)))';
  const totalsRow = hasTotals ? (
    <tr style={{ background: totalsRowBg }}>
      <th
        scope="row"
        className={cn("sticky left-0 z-[100] shrink-0 overflow-hidden border-r border-border/60 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider", firstColClass)}
        style={{
          ...(firstColStyle ?? {}),
          background: totalsRowBg,
          boxShadow: '4px 0 8px -2px rgba(0,0,0,0.08)',
        }}
      >
        {totalLabel ? (
          totalLabelDropsTooltip && /^Total \([^)]+ drop[s]?\) \([^)]+ text[s]?\)$/.test(totalLabel) ? (
            (() => {
              const m = totalLabel.match(/^(Total \()([^)]+)(\) \()([^)]+)(\))$/);
              if (!m) return totalLabel;
              return (
                <>
                  {m[1]}
                  <span className="cursor-help underline decoration-dotted decoration-muted-foreground/60" title={totalLabelDropsTooltip}>
                    {m[2]}
                  </span>
                  {m[3]}
                  {m[4]}
                  {m[5]}
                </>
              );
            })()
          ) : (
            totalLabel
          )
        ) : typeof totalRowCount === 'number' && totalRowCount >= 0 ? (
          `Total (${totalRowCount.toLocaleString()})`
        ) : (
          'Total'
        )}
      </th>
      {renderTotalsCells(totals, columnDefs, currency)}
    </tr>
  ) : null;

  const filteredTotalsRow = showFilteredRow ? (
    <tr style={{ background: filteredRowBg }}>
      <th
        scope="row"
        className={cn("sticky left-0 z-[100] shrink-0 overflow-hidden border-r border-border/60 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider", firstColClass)}
        style={{
          ...(firstColStyle ?? {}),
          background: filteredRowBg,
          boxShadow: '4px 0 8px -2px rgba(0,0,0,0.08)',
        }}
      >
        Showing ({filteredRowCount!.toLocaleString()})
      </th>
      {renderTotalsCells(filteredTotals!, columnDefs, currency)}
    </tr>
  ) : null;

  const fullDatasetBg =
    'color-mix(in srgb, hsl(var(--muted-foreground)) 10%, hsl(var(--card)))';
  const fd = fullDatasetAggregate;
  const showFullDatasetRow =
    fd &&
    (fd.loading ||
      fd.error ||
      (fd.totals != null && typeof fd.rowCount === 'number'));

  const fullDatasetRow = showFullDatasetRow ? (
    <tr style={{ background: fullDatasetBg }}>
      <th
        scope="row"
        className={cn(
          'sticky left-0 z-[100] shrink-0 overflow-hidden border-r border-border/60 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider',
          firstColClass,
        )}
        style={{
          ...(firstColStyle ?? {}),
          background: fullDatasetBg,
          boxShadow: '4px 0 8px -2px rgba(0,0,0,0.08)',
        }}
      >
        {fd!.loading ? (
          <span className="inline-flex items-center gap-2 normal-case">
            <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden />
            Full dataset (filters applied)…
          </span>
        ) : fd!.error ? (
          <span className="normal-case font-medium text-destructive">
            Full dataset (filters) could not be loaded
          </span>
        ) : (
          <span className="normal-case">
            Full dataset (filters applied) — {fd!.rowCount!.toLocaleString()} phone+source row
            {fd!.rowCount === 1 ? '' : 's'}
            {fullDatasetFiltersStale ? (
              <span className="mt-1 block text-[10px] font-normal text-amber-700 dark:text-amber-500">
                Bar filters changed — click Apply to full dataset to refresh this row.
              </span>
            ) : null}
          </span>
        )}
      </th>
      {fd!.loading || fd!.error
        ? columnDefs.map((col) => (
            <td
              key={col.key}
              className="min-w-[4.5rem] px-2 py-2.5 text-right tabular-nums text-muted-foreground"
            >
              —
            </td>
          ))
        : renderTotalsCells(fd!.totals!, columnDefs, currency)}
    </tr>
  ) : null;

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <div className="min-h-0 min-w-0 flex-1 overflow-auto">
        <table className="w-full min-w-max border-collapse text-sm">
          <colgroup>
            <col className={firstColClass} style={firstColStyle} />
            {columnDefs.map((col) => (
              <col key={col.key} style={{ width: 80 }} />
            ))}
          </colgroup>
          <thead className="sticky top-0 z-10 border-b border-border bg-card backdrop-blur-sm">
            <tr>
          <th
            role="button"
            tabIndex={0}
            onClick={() => onSortClick(firstColumnSortKey)}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSortClick(firstColumnSortKey)}
            className={cn(
              'sticky left-0 z-[100] border-r border-border group px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer select-none transition-colors hover:bg-muted whitespace-nowrap',
              firstColClass,
              sort?.field === firstColumnSortKey
                ? sort.dir === 'desc'
                  ? 'bg-primary/15 text-primary'
                  : 'bg-destructive/15 text-destructive'
                : 'text-muted-foreground',
            )}
            style={{
              ...(firstColStyle ?? {}),
              background:
                sort?.field === firstColumnSortKey && sort?.dir === 'desc'
                  ? 'color-mix(in srgb, hsl(var(--primary)) 15%, hsl(var(--card)))'
                  : sort?.field === firstColumnSortKey && sort?.dir === 'asc'
                    ? 'color-mix(in srgb, hsl(var(--destructive)) 15%, hsl(var(--card)))'
                    : 'hsl(var(--card))',
              boxShadow: '4px 0 8px -2px rgba(0,0,0,0.06)',
            }}
          >
            <span className="inline-flex items-center gap-1 truncate" title={`${firstColumnLabel} (sortable)`}>
              {firstColumnLabel}
              {sort?.field === firstColumnSortKey ? (
                sort.dir === 'desc' ? (
                  <ArrowDown className="h-3 w-3" aria-label="Sorted Best (Desc)" />
                ) : (
                  <ArrowUp className="h-3 w-3" aria-label="Sorted Least (Asc)" />
                )
              ) : (
                <ArrowUpDown className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-50" />
              )}
            </span>
          </th>
          {columnDefs.map((col) => {
            const isActive = sort?.field === col.key;
            const accent = 'columnAccent' in col ? (col as { columnAccent?: 'conv' | 'reg' | 'ftd' }).columnAccent : undefined;
            const headerStyle =
              accent === 'conv'
                ? { background: 'hsl(var(--stat-conv-header))', color: 'hsl(var(--stat-conv-value))' }
                : accent === 'reg'
                  ? { background: 'hsl(var(--stat-reg-header))', color: 'hsl(var(--stat-reg-value))' }
                  : accent === 'ftd'
                    ? { background: 'hsl(var(--stat-ftd-header))', color: 'hsl(var(--stat-ftd-value))' }
                    : col.key === 'profit'
                      ? { background: 'hsl(var(--stat-profit-header))', color: 'hsl(var(--stat-profit-value))' }
                      : col.key === 'cost'
                        ? { background: 'hsl(var(--stat-cost-header))', color: 'hsl(var(--stat-cost-value))' }
                        : undefined;
            const hasColoredHeader = !!headerStyle;
            return (
              <th
                key={col.key}
                role="button"
                tabIndex={0}
                onClick={() => onSortClick(col.key)}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSortClick(col.key)}
                className={cn(
                  'group min-w-[4.5rem] whitespace-nowrap px-2 py-2.5 text-right text-xs font-semibold uppercase tracking-wider cursor-pointer select-none transition-colors font-medium',
                  hasColoredHeader ? 'hover:opacity-90' : 'hover:bg-muted',
                  !hasColoredHeader &&
                    (isActive
                      ? sort?.dir === 'desc'
                        ? 'bg-primary/15 text-primary'
                        : 'bg-destructive/15 text-destructive'
                      : 'text-muted-foreground'),
                )}
                style={headerStyle}
              >
                <span className="inline-flex items-center justify-end gap-1">
                  {isActive &&
                    (sort?.dir === 'desc' ? (
                      <ArrowDown className="h-3 w-3 shrink-0" aria-label="Sorted Best (Desc)" />
                    ) : (
                      <ArrowUp className="h-3 w-3 shrink-0" aria-label="Sorted Least (Asc)" />
                    ))}
                  {!isActive && (
                    <ArrowUpDown className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-50" />
                  )}
                  {col.label}
                  {col.tooltip ? (
                    <span
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      className="inline-flex"
                    >
                      <Tooltip
                        side="top"
                        interactive
                        content={
                          <span className="block max-w-[18rem] text-xs font-normal normal-case leading-relaxed tracking-normal">
                            {col.tooltip}
                          </span>
                        }
                      >
                        <Info className="h-3 w-3 shrink-0 cursor-help opacity-60 transition-opacity hover:opacity-100" />
                      </Tooltip>
                    </span>
                  ) : null}
                </span>
              </th>
            );
          })}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {hasTotals && totalsRow}
            {filteredTotalsRow}
            {fullDatasetRow}
            {isGrouped
                ? groups.map((grp) => {
                  const isCollapsed = collapsedGroups.has(grp.key);
                  const hasSubGroups = !!(grp.subGroups && grp.subGroups.length > 0);

                  const renderGroupRow = (
                    g: { key: string; label: string; rows: GenericStatsRow[]; totals: Record<string, number> },
                    isNested: boolean,
                    skipRows?: boolean,
                  ) => {
                    const gCollapsed = collapsedGroups.has(g.key);
                    const gProfit = getVoluumValueForDisplay(g.totals, 'profit');
                    const gProfitBorderColor =
                      gProfit != null && gProfit > 0
                        ? 'hsl(var(--success))'
                        : gProfit != null && gProfit < 0
                          ? 'hsl(var(--destructive))'
                          : 'hsl(var(--muted-foreground) / 0.5)';
                    return (
                      <Fragment key={g.key}>
                        <tr
                          role="button"
                          tabIndex={0}
                          onClick={() => onToggleGroup(g.key)}
                          onKeyDown={(e) =>
                            (e.key === 'Enter' || e.key === ' ') &&
                            (e.preventDefault(), onToggleGroup(g.key))
                          }
                          className={cn(
                            'border-b border-border/40 transition-colors cursor-pointer',
                            isNested
                              ? 'bg-[hsl(214_18%_92%)] hover:bg-[hsl(214_18%_89%)] dark:bg-[hsl(214_18%_12%)] dark:hover:bg-[hsl(214_18%_15%)]'
                              : 'bg-[hsl(214_18%_88%)] hover:bg-[hsl(214_18%_85%)] dark:bg-[hsl(214_18%_14%)] dark:hover:bg-[hsl(214_18%_17%)]',
                          )}
                          aria-expanded={!gCollapsed}
                        >
                          <th
                            scope="row"
                            colSpan={1}
                            className={cn(
                              'sticky left-0 z-[100] border-r border-border/60 text-left',
                              isNested ? 'pl-8 py-2' : 'px-3 py-2.5',
                              firstColClass,
                            )}
                            style={{
                              ...(firstColStyle ?? {}),
                              background: 'inherit',
                              boxShadow: `inset 5px 0 0 0 ${gProfitBorderColor}, 4px 0 8px -2px rgba(0,0,0,0.04)`,
                            }}
                            title={
                              typeof gProfit === 'number' ? `Profit: ${gProfit.toFixed(2)}` : undefined
                            }
                          >
                            <div className="flex min-w-0 items-center gap-2">
                              {gCollapsed ? (
                                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                              ) : (
                                <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                              )}
                              <span className="truncate text-sm font-medium text-foreground">
                                {g.label}
                              </span>
                              <span className="shrink-0 text-xs font-normal text-muted-foreground">
                                {g.rows.length} {rowUnitLabel}{g.rows.length !== 1 ? 's' : ''}
                              </span>
                            </div>
                          </th>
                          {columnDefs.map((col) => {
                            const v = getVoluumValueForDisplay(g.totals, col.key);
                            const formatted = formatMetricValue(v, col, currency);
                            const numVal = typeof v === 'number' ? v : null;
                            const hint = col.colorHint;
                            const accent =
                              'columnAccent' in col
                                ? (col as { columnAccent?: 'conv' | 'reg' | 'ftd' }).columnAccent
                                : undefined;
                            let colorClass = 'text-foreground font-medium';
                            let valueStyle: React.CSSProperties | undefined;
                            if (accent && numVal != null && numVal > 0) {
                              const varName =
                                accent === 'conv'
                                  ? '--stat-conv-value'
                                  : accent === 'reg'
                                    ? '--stat-reg-value'
                                    : '--stat-ftd-value';
                              valueStyle = { color: `hsl(var(${varName}))` };
                            } else if (hint === 'money' && numVal != null) {
                              if (numVal > 0) colorClass = 'text-success font-medium';
                              else if (numVal < 0) colorClass = 'text-destructive font-medium';
                            } else if (hint === 'negative' && numVal != null) {
                              colorClass = 'text-destructive font-medium';
                            }
                            return (
                              <td
                                key={col.key}
                                className={cn(
                                  'min-w-[4.5rem] px-2 py-2 text-right tabular-nums whitespace-nowrap',
                                  colorClass,
                                )}
                                style={valueStyle}
                              >
                                {formatted}
                              </td>
                            );
                          })}
                        </tr>
                        {!gCollapsed &&
                          !skipRows &&
                          g.rows.map((r, index) => (
                            <StatsRow
                              key={`${g.key}::${r.id}::${index}`}
                              row={r}
                              index={index}
                              currency={currency}
                              columnDefs={columnDefs}
                              isSelected={selectedRows.some((s) => s.id === r.id)}
                              onSelect={() =>
                                setSelectedRows((prev) =>
                                  prev.some((s) => s.id === r.id)
                                    ? prev.filter((s) => s.id !== r.id)
                                    : [...prev, r],
                                )
                              }
                              firstCellContent={renderFirstCell(r)}
                              indentAsChild
                              firstColumnClassName={firstColumnClassName}
                              firstColumnStyle={firstColStyle}
                            />
                          ))}
                      </Fragment>
                    );
                  };

                  return (
                    <Fragment key={grp.key}>
                      {renderGroupRow(grp, false, hasSubGroups)}
                      {!isCollapsed && hasSubGroups &&
                        grp.subGroups!.map((sg) => renderGroupRow(sg, true))}
                    </Fragment>
                  );
                })
              : rows.map((r, index) => (
              <StatsRow
                key={`${r.id}::${index}`}
                row={r}
                index={index}
                currency={currency}
                columnDefs={columnDefs}
                isSelected={selectedRows.some((s) => s.id === r.id)}
                onSelect={() =>
                  setSelectedRows((prev) =>
                    prev.some((s) => s.id === r.id)
                      ? prev.filter((s) => s.id !== r.id)
                      : [...prev, r],
                  )
                }
                firstCellContent={renderFirstCell(r)}
                firstColumnClassName={firstColumnClassName}
                firstColumnStyle={firstColStyle}
              />
                ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
